import React, { useEffect, useState, useContext, useCallback, useMemo } from 'react';
import {
  Alert, Button, Descriptions, Segmented, Select, Spin, Table, Tag, Typography, Upload, message, Modal, Popconfirm, DatePicker,
} from 'antd';
import { InboxOutlined, CloudUploadOutlined, TableOutlined, EditOutlined, DownloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import api from '../api';
import PaycorState from '../components/PaycorState';
import { UserContext, hasGrant } from '../App';

const { Dragger } = Upload;
const { Title, Text, Paragraph } = Typography;

const fmtMoney = v => v == null
  ? '—'
  : `${Number(v) < 0 ? '-' : ''}$${Math.abs(Number(v)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const MoneyCell = ({ v }) => (
  <span style={{ color: Number(v) < 0 ? '#cf1322' : undefined, fontVariantNumeric: 'tabular-nums' }}>
    {fmtMoney(v)}
  </span>
);

const PlanTag = ({ v }) => <Tag color={v === 'BCN' ? 'purple' : 'blue'}>{v}</Tag>;
const TypeTag = ({ v }) => v === 'ADJUSTMENT'
  ? <Tag color="red">Adjustment</Tag>
  : <Tag color="green">Current</Tag>;


export default function BCBSBillUploadPage() {
  const currentUser = useContext(UserContext);
  // Admin or the page grant, matching the route guard and the API. This was
  // Role === 'admin' alone, so a user granted the page reached it and was
  // then told they needed admin: every control on it was gated on the role
  // rather than on being allowed to use the page.
  const canUse      = currentUser?.Role === 'admin' || hasGrant(currentUser, 'bcbs-bill');

  const [view,  setView]  = useState('upload');
  const [phase, setPhase] = useState('upload');   // upload | preview | done

  const [file,       setFile]       = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [rows,       setRows]       = useState([]);
  const [ambiguous,  setAmbiguous]  = useState([]);
  const [duplicates, setDuplicates] = useState([]);
  const [result,     setResult]     = useState(null);

  const [storedDupes,        setStoredDupes]        = useState([]);
  const [storedDupesLoading, setStoredDupesLoading] = useState(false);

  const [qaUsers,       setQaUsers]       = useState([]);
  const [qaUsersLoading, setQaUsersLoading] = useState(true);

  // Assignments keyed by MemberKey (LASTNAME-<contract>), never by row index
  const [assignments, setAssignments] = useState({});

  const [unlinked,        setUnlinked]        = useState([]);
  const [unlinkedLoading, setUnlinkedLoading] = useState(false);
  const [linkDraft,       setLinkDraft]       = useState({});
  const [savingLinks,     setSavingLinks]     = useState(new Set());

  // Browse — filter by the month a charge applies to, or the date it was billed
  const [filterBy,       setFilterBy]       = useState('charge_month');
  // Duplicate-policy detail starts collapsed: the headline carries the
  // warning, the table behind it can run to dozens of rows.
  const [dupesOpen,      setDupesOpen]      = useState(false);
  const [summary,        setSummary]        = useState([]);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [months,         setMonths]         = useState([]);
  const [billedDates,    setBilledDates]    = useState([]);
  const [monthsLoading,  setMonthsLoading]  = useState(false);
  const [browseMonth,    setBrowseMonth]    = useState(null);
  const [browseBilled,   setBrowseBilled]   = useState(null);
  const [charges,        setCharges]        = useState([]);
  const [chargesLoading, setChargesLoading] = useState(false);

  useEffect(() => {
    if (!canUse) return;
    api.get('/api/admin/bcbs-bill/qa-users')
      .then(r => setQaUsers(r.data || []))
      .catch(() => {})
      .finally(() => setQaUsersLoading(false));
  }, [canUse]);

  useEffect(() => {
    if (!canUse || view !== 'browse') return;
    setMonthsLoading(true);
    api.get('/api/admin/bcbs-bill/months')
      .then(r => {
        const bm = r.data?.charge_months  || [];
        const bd = r.data?.billed_dates || [];
        setMonths(bm);
        setBilledDates(bd);
        setBrowseMonth(prev  => prev ?? (bm.length ? bm[0] : null));
        setBrowseBilled(prev => prev ?? (bd.length ? bd[0] : null));
      })
      .catch(() => { setMonths([]); setBilledDates([]); })
      .finally(() => setMonthsLoading(false));
  }, [canUse, view]);

  const activeFilter = filterBy === 'charge_month' ? browseMonth : browseBilled;

  // Named so a relink can refresh the list without re-deriving the params.
  const loadCharges = useCallback(() => {
    if (!canUse || view !== 'browse' || !activeFilter) { setCharges([]); return; }
    setChargesLoading(true);
    const params = filterBy === 'charge_month'
      ? { charge_month: activeFilter }
      : { date_billed: activeFilter };
    api.get('/api/admin/bcbs-bill/charges', { params })
      .then(r => setCharges(r.data || []))
      .catch(() => setCharges([]))
      .finally(() => setChargesLoading(false));
  }, [canUse, view, filterBy, activeFilter]);

  useEffect(() => { loadCharges(); }, [loadCharges]);

  // By Member: flat rows from the API, pivoted here so the month columns follow
  // whatever invoice months are actually present.
  const [byMember, setByMember] = useState([]);
  const [byMemberLoading, setByMemberLoading] = useState(false);
  const [memberFrom, setMemberFrom] = useState(null);
  const [memberTo,   setMemberTo]   = useState(null);

  // Default to the full span of invoice months on file, so the view opens
  // showing everything and the pickers narrow it rather than hiding data on
  // first load. Only seeds once; after that the choice is the operator's.
  useEffect(() => {
    if (!billedDates.length || memberFrom || memberTo) return;
    const months = billedDates.map(d => String(d).slice(0, 7)).sort();
    setMemberFrom(dayjs(`${months[0]}-01`));
    setMemberTo(dayjs(`${months[months.length - 1]}-01`));
  }, [billedDates, memberFrom, memberTo]);

  useEffect(() => {
    if (!canUse || view !== 'browse' || filterBy !== 'member') return;
    setByMemberLoading(true);
    api.get('/api/admin/bcbs-bill/by-member', {
      params: {
        ...(memberFrom ? { month_from: memberFrom.format('YYYY-MM') } : {}),
        ...(memberTo   ? { month_to:   memberTo.format('YYYY-MM')   } : {}),
      },
    })
      .then(r => setByMember(r.data || []))
      .catch(() => setByMember([]))
      .finally(() => setByMemberLoading(false));
  }, [canUse, view, filterBy, memberFrom, memberTo]);

  const memberPivot = useMemo(() => {
    const months = [...new Set(byMember.map(r => r.InvoiceMonth).filter(Boolean))].sort();
    // Plan/Type combinations are collected from the data rather than assumed, so
    // a new plan appearing on a future bill shows up without a code change.
    const combos = [...new Set(byMember.map(r => `${r.PlanType}||${r.ChargeType}`))]
      .sort((a, b) => a.localeCompare(b));
    const byKey = {};
    byMember.forEach(r => {
      if (!byKey[r.MemberKey]) {
        byKey[r.MemberKey] = {
          MemberKey: r.MemberKey, LastName: r.LastName, FirstName: r.FirstName,
          ContractNumber: r.ContractNumber, QAUserName: r.QAUserName,
          PaycorStatus: r.PaycorStatus,
          PaycorTermDate: r.PaycorTermDate,
          PaycorHireDate: r.PaycorHireDate, Total: 0,
        };
      }
      const cell = `${r.InvoiceMonth}||${r.PlanType}||${r.ChargeType}`;
      byKey[r.MemberKey][cell] = (byKey[r.MemberKey][cell] || 0) + r.Amount;
      byKey[r.MemberKey][`__m__${r.InvoiceMonth}`] =
        (byKey[r.MemberKey][`__m__${r.InvoiceMonth}`] || 0) + r.Amount;
      byKey[r.MemberKey].Total += r.Amount;
    });
    const rows = Object.values(byKey).sort((a, b) =>
      (a.LastName || '').localeCompare(b.LastName || '')
      || (a.FirstName || '').localeCompare(b.FirstName || ''));
    return { months, combos, rows };
  }, [byMember]);


  // Optional hire-date window over the By Member rows. Held as strings because
  // PaycorHireDate is an ISO date and ISO dates compare correctly as text.
  //
  // A member with no hire date drops out as soon as either bound is set: an
  // unlinked member, or one whose QA user has no Paycor record, has no hire
  // date to fall inside the window. That is the honest answer to "hired
  // between these dates" rather than a convenience, so the count beside the
  // picker says how many were set aside.
  const [hireFrom, setHireFrom] = useState(null);
  const [hireTo,   setHireTo]   = useState(null);

  const memberRows = useMemo(() => {
    const lo = hireFrom?.format('YYYY-MM-DD');
    const hi = hireTo?.format('YYYY-MM-DD');
    if (!lo && !hi) return memberPivot.rows;
    return memberPivot.rows.filter((r) => {
      const d = r.PaycorHireDate;
      if (!d) return false;
      if (lo && d < lo) return false;
      if (hi && d > hi) return false;
      return true;
    });
  }, [memberPivot.rows, hireFrom, hireTo]);

  const hireHidden = memberPivot.rows.length - memberRows.length;

  // Correcting a member -> QA user link. Held per member, not per charge row:
  // the link covers every charge that member has.
  const [relinkRow,    setRelinkRow]    = useState(null);
  const [relinkUserId, setRelinkUserId] = useState(null);
  const [relinkSaving, setRelinkSaving] = useState(false);

  const openRelink = (row) => {
    setRelinkRow(row);
    setRelinkUserId(qaUsers.find(u => u.DisplayName === row.QAUserName)?.ID ?? null);
  };

  const submitRelink = async (qaUserId) => {
    if (!relinkRow) return;
    setRelinkSaving(true);
    try {
      await api.post('/api/admin/bcbs-bill/relink', {
        member_key: relinkRow.MemberKey,
        qa_user_id: qaUserId,
      });
      message.success(qaUserId ? 'Member reassigned.' : 'Link cleared.');
      setRelinkRow(null);
      loadCharges();
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to update the link.');
    } finally {
      setRelinkSaving(false);
    }
  };

  // Loaded only when the summary is on screen: it scans the whole table rather
  // than one month, so there is no reason to pay for it on the other tabs.
  useEffect(() => {
    if (!canUse || view !== 'browse' || filterBy !== 'summary') return;
    setSummaryLoading(true);
    api.get('/api/admin/bcbs-bill/invoice-summary')
      .then(r => setSummary(r.data || []))
      .catch(() => setSummary([]))
      .finally(() => setSummaryLoading(false));
  }, [canUse, view, filterBy]);

  // Double-billing audit over stored data, scoped to the selected charge month
  useEffect(() => {
    if (!canUse || view !== 'browse') { setStoredDupes([]); return; }
    setStoredDupesLoading(true);
    const params = filterBy === 'charge_month' && activeFilter
      ? { charge_month: activeFilter }
      : {};
    api.get('/api/admin/bcbs-bill/duplicates', { params })
      .then(r => setStoredDupes(r.data || []))
      .catch(() => setStoredDupes([]))
      .finally(() => setStoredDupesLoading(false));
  }, [canUse, view, filterBy, activeFilter]);

  const qaUserOptions = qaUsers.map(u => ({
    label: u.IsActive ? u.DisplayName : `${u.DisplayName} (inactive)`,
    value: u.ID,
  }));

  const loadUnlinked = () => {
    setUnlinkedLoading(true);
    api.get('/api/admin/bcbs-bill/unlinked')
      .then(r => setUnlinked(r.data || []))
      .catch(() => setUnlinked([]))
      .finally(() => setUnlinkedLoading(false));
  };

  const saveLink = async (memberKey) => {
    const qaUserId = linkDraft[memberKey];
    if (!qaUserId) return;
    setSavingLinks(prev => new Set([...prev, memberKey]));
    try {
      await api.post('/api/admin/bcbs-bill/link', { member_key: memberKey, qa_user_id: qaUserId });
      message.success('Member linked. All of their charges are now claimed.');
      setUnlinked(prev => prev.filter(u => u.MemberKey !== memberKey));
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to link member.');
    } finally {
      setSavingLinks(prev => { const s = new Set(prev); s.delete(memberKey); return s; });
    }
  };

  const handlePreview = async () => {
    if (!file) { message.warning('Select a file first.'); return; }
    setPreviewing(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await api.post('/api/admin/bcbs-bill/preview', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const data = res.data.rows || [];
      setRows(data);
      setAmbiguous(res.data.ambiguous || []);
      setDuplicates(res.data.duplicates || []);
      const init = {};
      data.forEach(r => { if (r.QAUserID) init[r.MemberKey] = r.QAUserID; });
      setAssignments(init);
      setPhase('preview');
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to parse file.');
    } finally {
      setPreviewing(false);
    }
  };

  const handleConfirm = async () => {
    setConfirming(true);
    const payload = rows.map(r => ({ ...r, QAUserID: assignments[r.MemberKey] ?? null }));
    try {
      const res = await api.post('/api/admin/bcbs-bill/confirm', { rows: payload });
      setResult(res.data);
      setPhase('done');
      loadUnlinked();
    } catch (err) {
      message.error(err.response?.data?.error || 'Upload failed.');
    } finally {
      setConfirming(false);
    }
  };

  const reset = () => {
    setPhase('upload'); setFile(null); setRows([]);
    setAssignments({}); setResult(null); setAmbiguous([]); setDuplicates([]);
  };

  const matchedRows      = rows.filter(r => r.matched).length;
  const unresolvedMembers = [...new Set(
    rows.filter(r => !assignments[r.MemberKey]).map(r => r.MemberKey)
  )].length;
  const distinctMembers  = [...new Set(rows.map(r => r.MemberKey))].length;
  const previewTotal     = rows.reduce((s, r) => s + (Number(r.Amount) || 0), 0);

  const chargesTotal     = charges.reduce((s, r) => s + (Number(r.Amount) || 0), 0);
  const chargesUnlinked  = charges.filter(r => !r.QAUserName).length;

  // Members flagged as double-billed, for highlighting their rows in the table
  const dupeKeys = new Set(duplicates.map(d => `${d.MemberKey}|${d.ChargeMonth}`));

  // ── Excel export ──────────────────────────────────────────────────────────
  // A workbook of named sheets; each sheet is plain objects with the header as
  // the key, the same shape the exports elsewhere in the app use. Sheets with
  // no rows are dropped rather than written empty.
  const xlsxDownload = (sheets, filename) => {
    const wb = XLSX.utils.book_new();
    let any = false;
    sheets.forEach(({ name, rows }) => {
      if (!rows.length) return;
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), name);
      any = true;
    });
    if (!any) { message.warning('No data to export.'); return; }
    XLSX.writeFile(wb, filename);
  };

  const exportExcel = () => {
    if (filterBy === 'summary') {
      xlsxDownload([{
        name: 'Upload Summary',
        rows: summary.map(r => ({
          'Date Billed':        r.DateBilled,
          'Invoice #':          r.InvoiceNumber || '',
          'First Charge Month': r.FirstMonth || '',
          'Last Charge Month':  r.LastMonth || '',
          'BCBS Rows':          r.BCBSRows,
          'BCN Rows':           r.BCNRows,
          'Members':            r.Members,
          'Rows':               r.Rows,
          'Current Rows':       r.CurrentRows,
          'Adjustment Rows':    r.AdjustmentRows,
          'Unattributed':       r.Unattributed,
          'Net Amount':         r.NetAmount,
          'Uploaded':           r.UploadedAt ? String(r.UploadedAt).replace('T', ' ').substring(0, 16) : '',
        })),
      }], 'bcbs_upload_summary.xlsx');
      return;
    }

    if (filterBy === 'member') {
      const pivot = memberRows.map(r => {
        const row = {
          'Member':            [r.LastName, r.FirstName].filter(Boolean).join(', '),
          'Contract #':        r.ContractNumber || '',
          'QA User':           r.QAUserName || 'Unattributed',
          'Employment Status': r.PaycorStatus || '',
          'Term Date':         r.PaycorTermDate || '',
          'Hire Date':         r.PaycorHireDate || '',
        };
        // Invoice months, matching the columns on screen.
        memberPivot.months.forEach(m => { row[m] = r[`__m__${m}`] ?? null; });
        row['Total'] = r.Total;
        return row;
      });
      // Each month cell on screen also carries a plan/type split that one total
      // per month cannot hold, so the flat rows behind the pivot ride along on
      // their own sheet instead of being dropped.
      const keep = new Set(memberRows.map((r) => r.MemberKey));
      const detail = byMember.filter((r) => keep.has(r.MemberKey)).map(r => ({
        'Member':            [r.LastName, r.FirstName].filter(Boolean).join(', '),
        'Contract #':        r.ContractNumber || '',
        'QA User':           r.QAUserName || 'Unattributed',
        'Employment Status': r.PaycorStatus || '',
        'Term Date':         r.PaycorTermDate || '',
        'Hire Date':         r.PaycorHireDate || '',
        'Invoice Month':     r.InvoiceMonth,
        'Plan':              r.PlanType,
        'Type':              r.ChargeType,
        'Amount':            r.Amount,
      }));
      const span = [memberFrom && memberFrom.format('YYYY-MM'), memberTo && memberTo.format('YYYY-MM')]
        .filter(Boolean).join('_to_');
      xlsxDownload(
        [{ name: 'By Member', rows: pivot }, { name: 'Detail', rows: detail }],
        `bcbs_by_member${span ? `_${span}` : ''}.xlsx`,
      );
      return;
    }

    xlsxDownload([{
      name: 'Charges',
      rows: charges.map(r => ({
        'Member':            [r.LastName, r.FirstName].filter(Boolean).join(', '),
        'Contract #':        r.ContractNumber || '',
        'QA User':           r.QAUserName || 'Unattributed',
        'Employment Status': r.PaycorStatus || '',
        'Term Date':         r.PaycorTermDate || '',
        'Plan':              r.PlanType,
        'Type':              r.ChargeType,
        'Amount':            r.Amount,
        'Package':           r.BenefitPackage || '',
        'Coverage':          r.RateTierDetail || '',
        'Charge Month':      r.ChargeMonth || '',
        'Period From':       r.PeriodFrom ? String(r.PeriodFrom).substring(0, 10) : '',
        'Period Through':    r.PeriodThrough ? String(r.PeriodThrough).substring(0, 10) : '',
        'Remarks':           r.Remarks || '',
        'Date Billed':       r.DateBilled ? String(r.DateBilled).substring(0, 10) : '',
        'Invoice #':         r.InvoiceNumber || '',
      })),
    }], `bcbs_charges_${activeFilter || 'all'}.xlsx`);
  };

  const exportDisabled = filterBy === 'summary' ? summary.length === 0
    : filterBy === 'member' ? memberRows.length === 0
    : charges.length === 0;

  const memberCell = (r) => (
    <div>
      <div style={{ fontWeight: 600, fontSize: 13 }}>
        {[r.LastName, r.FirstName].filter(Boolean).join(', ') || '(no name)'}
      </div>
      <Text type="secondary" style={{ fontSize: 11, fontFamily: 'monospace' }}>{r.ContractNumber}</Text>
    </div>
  );

  const previewCols = [
    {
      title: 'Member',
      key: 'member',
      width: 210,
      render: (_, r) => (
        <div>
          {memberCell(r)}
          {r.ChargeType === 'CURRENT' && dupeKeys.has(`${r.MemberKey}|${r.ChargeMonth}`) && (
            <Tag color="red" style={{ marginTop: 3 }}>Double billed</Tag>
          )}
        </div>
      ),
    },
    {
      title: 'QA User',
      key: 'qaUser',
      width: 230,
      render: (_, r) => r.matched ? (
        <div>
          <Tag color="green" style={{ marginBottom: 2 }}>Auto-matched</Tag>
          <div style={{ fontSize: 12, color: '#595959' }}>{r.DisplayName}</div>
        </div>
      ) : (
        <Select
          size="small"
          style={{ width: '100%' }}
          placeholder="Select QA user..."
          value={assignments[r.MemberKey]}
          options={qaUserOptions}
          showSearch
          filterOption={(input, opt) => (opt?.label ?? '').toLowerCase().includes(input.toLowerCase())}
          onChange={v => setAssignments(prev => ({ ...prev, [r.MemberKey]: v }))}
          loading={qaUsersLoading}
          allowClear
          onClear={() => setAssignments(prev => {
            const n = { ...prev }; delete n[r.MemberKey]; return n;
          })}
        />
      ),
    },
    { title: 'Plan', dataIndex: 'PlanType',   key: 'PlanType',   width: 80,  render: v => <PlanTag v={v} /> },
    { title: 'Type', dataIndex: 'ChargeType', key: 'ChargeType', width: 110, render: v => <TypeTag v={v} /> },
    { title: 'Amount', dataIndex: 'Amount', key: 'Amount', width: 110, align: 'right',
      render: v => <MoneyCell v={v} /> },
    { title: 'Charge Month', dataIndex: 'ChargeMonth', key: 'ChargeMonth', width: 100,
      render: v => v || <Text type="secondary">—</Text> },
    { title: 'Package', dataIndex: 'BenefitPackage', key: 'BenefitPackage', width: 110,
      render: v => v || <Text type="secondary">—</Text> },
    { title: 'Coverage', dataIndex: 'RateTierDetail', key: 'RateTierDetail', width: 180, ellipsis: true,
      render: v => v || <Text type="secondary">—</Text> },
    { title: 'Remarks', dataIndex: 'Remarks', key: 'Remarks', width: 90,
      render: v => v || <Text type="secondary">—</Text> },
    { title: 'Date Billed', dataIndex: 'DateBilled', key: 'DateBilled', width: 110,
      render: v => v ? String(v).substring(0, 10) : <Text type="secondary">—</Text> },
    { title: 'Invoice #', dataIndex: 'InvoiceNumber', key: 'InvoiceNumber', width: 120,
      render: v => <Text type="secondary" style={{ fontSize: 11, fontFamily: 'monospace' }}>{v || '—'}</Text> },
  ];

  return (
    <div style={{ padding: '16px 20px' }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: 16, gap: 16, flexWrap: 'wrap',
      }}>
        <Title level={4} style={{ margin: 0 }}>BCBS / BCN Bill</Title>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          {view === 'upload' && phase !== 'upload' && (
            <Button onClick={reset}>Upload Another</Button>
          )}
          <Segmented
            value={view}
            onChange={setView}
            options={[
              { label: 'Upload',        value: 'upload', icon: <CloudUploadOutlined /> },
              { label: 'View Uploaded', value: 'browse', icon: <TableOutlined /> },
            ]}
          />
        </div>
      </div>

      {!canUse && (
        <Alert type="error" message="You do not have access to this page." style={{ marginBottom: 16 }} showIcon />
      )}

      {/* ── UPLOAD ────────────────────────────────────────────────────────── */}
      {canUse && view === 'upload' && phase === 'upload' && (
        <>
          <Paragraph type="secondary" style={{ marginBottom: 20, maxWidth: 900 }}>
            Upload the Export / Print Invoice Report from Blue Cross Blue Shield of Michigan.
            One file may contain several invoices in both the <strong>BCBS</strong> and{' '}
            <strong>BCN</strong> layouts — all are read in a single pass. Both current charges
            and membership adjustments are imported; adjustments are stored as credits and are
            attributed to the month they apply to, not the month billed. Members are identified
            by last name plus contract number.
          </Paragraph>

          <Dragger
            name="file"
            accept=".txt,.tsv,.csv"
            beforeUpload={f => { setFile(f); return false; }}
            onRemove={() => setFile(null)}
            fileList={file ? [file] : []}
            style={{ marginBottom: 16, maxWidth: 900 }}
          >
            <p className="ant-upload-drag-icon"><InboxOutlined /></p>
            <p className="ant-upload-text">Click or drag the BCBS invoice export here</p>
            <p className="ant-upload-hint">Tab-delimited .txt export. Invoice headers and totals are ignored.</p>
          </Dragger>

          <Button type="primary" onClick={handlePreview} loading={previewing} disabled={!file}>
            Preview &amp; Match
          </Button>
        </>
      )}

      {/* ── PREVIEW ───────────────────────────────────────────────────────── */}
      {canUse && view === 'upload' && phase === 'preview' && (
        <>
          {duplicates.length > 0 && (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
              message={
                <span>
                  Possible double billing — {duplicates.length} member
                  {duplicates.length > 1 ? 's are' : ' is'} on two or more active
                  policies{' '}
                  <Tag color="red" style={{ marginLeft: 4 }}>
                    {fmtMoney(duplicates.reduce((s, d) => s + (d.ExcessAmount || 0), 0))} above
                    a single policy each
                  </Tag>
                </span>
              }
              description={
                <>
                  <Paragraph type="secondary" style={{ marginBottom: 10, fontSize: 12 }}>
                    These members carry more than one <strong>current</strong> charge for the same
                    month, which usually means an old policy was never terminated after a plan
                    switch. Credits and adjustments are excluded, so a correctly handled switch
                    does not appear here. Worth querying with the carrier before paying.
                  </Paragraph>
                  <Table
                    rowKey={r => `${r.MemberKey}|${r.ChargeMonth}`}
                    dataSource={duplicates}
                    size="small"
                    pagination={false}
                    bordered
                    scroll={{ x: 'max-content' }}
                    columns={[
                      { title: 'Member', key: 'member', width: 190, render: (_, r) => memberCell(r) },
                      { title: 'Month', dataIndex: 'ChargeMonth', key: 'ChargeMonth', width: 90 },
                      {
                        title: 'Policies', key: 'plans', width: 190,
                        render: (_, r) => (
                          <div>
                            {r.Lines.map((l, i) => (
                              <div key={i} style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                                <PlanTag v={l.PlanType} />
                                <span style={{ color: '#8c8c8c' }}>{l.BenefitPackage}</span>
                                <span style={{ marginLeft: 6 }}>{fmtMoney(l.Amount)}</span>
                              </div>
                            ))}
                          </div>
                        ),
                      },
                      {
                        title: 'Billed', dataIndex: 'TotalAmount', key: 'TotalAmount',
                        width: 110, align: 'right', render: v => fmtMoney(v),
                      },
                      {
                        title: 'Excess', dataIndex: 'ExcessAmount', key: 'ExcessAmount',
                        width: 110, align: 'right',
                        render: v => <strong style={{ color: '#cf1322' }}>{fmtMoney(v)}</strong>,
                      },
                    ]}
                  />
                </>
              }
            />
          )}

          {ambiguous.length > 0 && (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
              message="Contract number shared by more than one surname"
              description={
                <>
                  <p style={{ marginBottom: 6 }}>
                    These contract numbers appear under two different last names in this file,
                    meaning two employees share the same masked suffix. They are treated as
                    separate people and must each be assigned individually — check them carefully
                    before confirming.
                  </p>
                  <div style={{ fontFamily: 'monospace', fontSize: 12 }}>
                    {ambiguous.map(c => <div key={c}>{c}</div>)}
                  </div>
                </>
              }
            />
          )}

          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16, alignItems: 'center' }}>
            <Tag color="green">{matchedRows} rows auto-matched</Tag>
            <Text type="secondary">{rows.length} rows · {distinctMembers} members</Text>
            <Text strong>Net: <MoneyCell v={previewTotal} /></Text>
            {unresolvedMembers > 0 && (
              <Alert
                type="warning"
                showIcon
                style={{ flex: 1, padding: '4px 12px', minWidth: 300 }}
                message={`${unresolvedMembers} member${unresolvedMembers > 1 ? 's' : ''} still need a QA user. You may leave them blank and link them afterwards.`}
              />
            )}
          </div>

          <Table
            rowKey={(_, i) => i}
            dataSource={rows}
            columns={previewCols}
            size="small"
            pagination={false}
            scroll={{ x: 'max-content', y: 600 }}
            bordered
            rowClassName={r => !assignments[r.MemberKey] ? 'ant-table-row-warning' : ''}
            style={{ marginBottom: 16 }}
          />

          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <Button type="primary" onClick={handleConfirm} loading={confirming}>
              Confirm Upload ({rows.length} rows)
            </Button>
            <Button onClick={reset}>Cancel</Button>
          </div>
        </>
      )}

      {/* ── DONE ──────────────────────────────────────────────────────────── */}
      {canUse && view === 'upload' && phase === 'done' && result && (
        <>
          <Alert
            type={(result.skipped > 0 || result.collided > 0) ? 'warning' : 'success'}
            showIcon
            style={{ marginBottom: 20 }}
            message={(result.skipped > 0 || result.collided > 0)
              ? 'Upload finished, but not every row was stored'
              : 'Upload complete'}
            description={
              <Descriptions size="small" column={3} style={{ marginTop: 8 }}>
                <Descriptions.Item label="Rows in file">{result.received}</Descriptions.Item>
                <Descriptions.Item label="Rows saved">{result.upserted}</Descriptions.Item>
                <Descriptions.Item label="Linked to QA user">
                  {result.upserted - (result.unlinked ?? 0)}
                </Descriptions.Item>
                <Descriptions.Item label="Saved unlinked">
                  {result.unlinked > 0 ? <Tag color="orange">{result.unlinked}</Tag> : <Tag color="green">0</Tag>}
                </Descriptions.Item>
                <Descriptions.Item label="Skipped">
                  {result.skipped > 0 ? <Tag color="red">{result.skipped}</Tag> : <Tag color="green">0</Tag>}
                </Descriptions.Item>
                <Descriptions.Item label="Overwrote a twin">
                  {result.collided > 0 ? <Tag color="red">{result.collided}</Tag> : <Tag color="green">0</Tag>}
                </Descriptions.Item>
              </Descriptions>
            }
          />

          <Title level={5} style={{ marginBottom: 4 }}>
            Unassigned Members
            {unlinked.length > 0 && <Tag color="orange" style={{ marginLeft: 8 }}>{unlinked.length}</Tag>}
          </Title>
          <Paragraph type="secondary" style={{ marginBottom: 12 }}>
            These members have charges but no QA user. Assigning one claims <strong>every</strong>{' '}
            charge for that member across both plans, including earlier uploads.
          </Paragraph>

          {unlinkedLoading ? <Spin /> : unlinked.length === 0 ? (
            <Alert type="success" showIcon style={{ marginBottom: 20 }}
                   message="Every member on file is linked to a QA user." />
          ) : (
            <Table
              rowKey="MemberKey"
              dataSource={unlinked}
              size="small"
              pagination={false}
              bordered
              style={{ marginBottom: 20 }}
              scroll={{ x: 'max-content' }}
              columns={[
                { title: 'Member', key: 'member', width: 190, render: (_, r) => memberCell(r) },
                {
                  title: 'Assign To',
                  key: 'assign',
                  width: 240,
                  render: (_, r) => (
                    <Select
                      size="small"
                      style={{ width: '100%' }}
                      placeholder="Select QA user..."
                      value={linkDraft[r.MemberKey]}
                      options={qaUserOptions}
                      showSearch
                      filterOption={(input, opt) => (opt?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                      onChange={v => setLinkDraft(prev => ({ ...prev, [r.MemberKey]: v }))}
                      loading={qaUsersLoading}
                      allowClear
                    />
                  ),
                },
                {
                  title: '', key: 'save', width: 80,
                  render: (_, r) => (
                    <Button
                      size="small"
                      type="primary"
                      disabled={!linkDraft[r.MemberKey]}
                      loading={savingLinks.has(r.MemberKey)}
                      onClick={() => saveLink(r.MemberKey)}
                    >
                      Link
                    </Button>
                  ),
                },
                { title: 'Months', key: 'months', width: 150,
                  render: (_, r) => r.FirstMonth === r.LastMonth
                    ? r.FirstMonth
                    : `${r.FirstMonth} – ${r.LastMonth}` },
                { title: 'Rows', dataIndex: 'ChargeRows', key: 'ChargeRows', width: 70, align: 'right' },
                { title: 'Net Amount', dataIndex: 'TotalAmount', key: 'TotalAmount', width: 120,
                  align: 'right', render: v => <MoneyCell v={v} /> },
              ]}
            />
          )}

          <Button type="primary" onClick={reset}>Upload Another Bill</Button>
        </>
      )}

      {/* ── VIEW UPLOADED ─────────────────────────────────────────────────── */}
      {canUse && view === 'browse' && (
        <>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
            <Segmented
              size="small"
              value={filterBy}
              onChange={setFilterBy}
              options={[
                { label: 'By Charge Month',  value: 'charge_month'  },
                { label: 'By Date Billed', value: 'date_billed' },
                { label: 'By Member', value: 'member' },
                { label: 'Upload Summary', value: 'summary' },
              ]}
            />
            {filterBy === 'summary' || filterBy === 'member' ? null : monthsLoading ? <Spin size="small" /> : filterBy === 'charge_month' ? (
              <Select
                style={{ minWidth: 160 }}
                placeholder="Select month"
                value={browseMonth}
                onChange={setBrowseMonth}
                options={months.map(m => ({ label: m, value: m }))}
                showSearch
                notFoundContent="Nothing uploaded yet"
              />
            ) : (
              <Select
                style={{ minWidth: 160 }}
                placeholder="Select date billed"
                value={browseBilled}
                onChange={setBrowseBilled}
                options={billedDates.map(d => ({ label: d, value: d }))}
                showSearch
                notFoundContent="Nothing uploaded yet"
              />
            )}
            {filterBy === 'summary' && summary.length > 0 && (
              <Text type="secondary">{summary.length} invoice{summary.length === 1 ? '' : 's'} uploaded</Text>
            )}
            {filterBy === 'member' && (
              <>
                <DatePicker
                  picker="month"
                  value={memberFrom}
                  onChange={setMemberFrom}
                  format="MMM YYYY"
                  placeholder="From"
                  style={{ width: 130 }}
                  disabledDate={d => memberTo && d && d.isAfter(memberTo, 'month')}
                />
                <Text type="secondary">to</Text>
                <DatePicker
                  picker="month"
                  value={memberTo}
                  onChange={setMemberTo}
                  format="MMM YYYY"
                  placeholder="To"
                  style={{ width: 130 }}
                  disabledDate={d => memberFrom && d && d.isBefore(memberFrom, 'month')}
                />
              </>
            )}
            {/* Optional: clear either end to stop bounding it. */}
            <DatePicker
              value={hireFrom}
              onChange={setHireFrom}
              format="YYYY-MM-DD"
              placeholder="Hired from"
              style={{ width: 140 }}
              disabledDate={(d) => hireTo && d && d.isAfter(hireTo, 'day')}
            />
            <DatePicker
              value={hireTo}
              onChange={setHireTo}
              format="YYYY-MM-DD"
              placeholder="Hired to"
              style={{ width: 140 }}
              disabledDate={(d) => hireFrom && d && d.isBefore(hireFrom, 'day')}
            />
            {hireHidden > 0 && (
              <Text type="secondary">
                {hireHidden} hidden (no hire date in range)
              </Text>
            )}
            {filterBy === 'member' && byMember.length > 0 && (
              <Text type="secondary">
                {memberRows.length} member{memberRows.length === 1 ? '' : 's'}
              </Text>
            )}
            {filterBy !== 'summary' && filterBy !== 'member' && charges.length > 0 && (
              <>
                <Text type="secondary">{charges.length} rows</Text>
                {chargesUnlinked > 0 && <Tag color="orange">{chargesUnlinked} unattributed</Tag>}
                <Text strong style={{ marginLeft: 'auto' }}>
                  Net: <MoneyCell v={chargesTotal} />
                </Text>
              </>
            )}
            <Button
              size="small"
              icon={<DownloadOutlined />}
              onClick={exportExcel}
              disabled={exportDisabled}
            >
              Export Excel
            </Button>
          </div>

          {/* Only alongside charges the operator is actually looking at. The
              duplicate check spans every stored month, so on a month with no
              rows on screen it warned about people who are not on the page. */}
          {filterBy !== 'summary' && filterBy !== 'member'
            && !storedDupesLoading && storedDupes.length > 0
            && charges.length > 0 && (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
              message={
                <span>
                  {storedDupes.length} member{storedDupes.length > 1 ? 's' : ''} on two or more
                  active policies{filterBy === 'charge_month' && activeFilter ? ` in ${activeFilter}` : ''}
                  <Tag color="red" style={{ marginLeft: 8 }}>
                    {fmtMoney(storedDupes.reduce((s, d) => s + (d.ExcessAmount || 0), 0))} excess
                  </Tag>
                  <Button
                    type="link"
                    size="small"
                    style={{ paddingLeft: 4 }}
                    onClick={() => setDupesOpen(o => !o)}
                  >
                    {dupesOpen ? 'Hide details' : `Show ${storedDupes.length} details`}
                  </Button>
                </span>
              }
              description={!dupesOpen ? null : (
                <Table
                  rowKey={r => `${r.MemberKey}|${r.ChargeMonth}`}
                  dataSource={storedDupes}
                  size="small"
                  pagination={false}
                  bordered
                  scroll={{ x: 'max-content' }}
                  columns={[
                    { title: 'Member', key: 'member', width: 190, render: (_, r) => memberCell(r) },
                    {
                      title: 'QA User', dataIndex: 'QAUserName', key: 'QAUserName', width: 190,
                      render: (v, r) => v ? (
                        <div>
                          <span>{v}</span>
                          <PaycorState status={r.PaycorStatus} termDate={r.PaycorTermDate} />
                        </div>
                      ) : <Tag color="orange">Unattributed</Tag>,
                    },
                    { title: 'Month', dataIndex: 'ChargeMonth', key: 'ChargeMonth', width: 90 },
                    {
                      title: 'Policies', key: 'count', width: 90, align: 'right',
                      render: (_, r) => `${r.ChargeCount} (${r.PlanCount} plan${r.PlanCount > 1 ? 's' : ''})`,
                    },
                    {
                      title: 'Billed', dataIndex: 'TotalAmount', key: 'TotalAmount',
                      width: 110, align: 'right', render: v => fmtMoney(v),
                    },
                    {
                      title: 'Excess', dataIndex: 'ExcessAmount', key: 'ExcessAmount',
                      width: 110, align: 'right',
                      render: v => <strong style={{ color: '#cf1322' }}>{fmtMoney(v)}</strong>,
                    },
                  ]}
                />
              )}
            />
          )}

          {filterBy === 'member' && (
            <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
              Month columns are <strong>invoice months</strong> (the month each charge was
              billed), not charge months.
            </Text>
          )}

          {filterBy === 'member' ? (
            byMemberLoading ? <Spin /> : memberPivot.rows.length === 0 ? (
              <Alert type="info" showIcon message="No invoices uploaded yet." />
            ) : (
              <Table
                rowKey="MemberKey"
                dataSource={memberRows}
                size="small"
                bordered
                pagination={false}
                sticky
                scroll={{ x: 'max-content' }}
                columns={[
                  {
                    title: 'Member', key: 'member', width: 200, fixed: 'left',
                    render: (_, r) => (
                      <div>
                        <Text strong>{r.LastName}, {r.FirstName}</Text>
                        <div style={{ fontSize: 11, color: '#999' }}>{r.ContractNumber}</div>
                      </div>
                    ),
                  },
                  {
                    title: 'QA User', key: 'qaUser', width: 190, fixed: 'left',
                    render: (_, r) => (
                      <div>
                        {r.QAUserName ? (
                          <span>
                            {r.QAUserName}
                          </span>
                        ) : <Tag color="orange">Unattributed</Tag>}
                        <PaycorState status={r.PaycorStatus} termDate={r.PaycorTermDate} />
                        {/* Always shown when known, unlike the state tag above:
                            when someone started is context for every row, not
                            just the ones worth chasing. The rehire date when it
                            is later than the hire date, otherwise the hire. */}
                        {r.PaycorHireDate && (
                          <div style={{ fontSize: 11, color: '#8c8c8c' }}>
                            Hired {r.PaycorHireDate}
                          </div>
                        )}
                      </div>
                    ),
                  },
                  // One column per invoice month holding that month's total, with
                  // the plan/type split inside the cell. As sub-columns this was
                  // five columns per month, so three months ran off the screen
                  // before the numbers you actually reconcile against.
                  ...memberPivot.months.map(m => ({
                    title: m,
                    key: m,
                    width: 150,
                    align: 'right',
                    render: (_, r) => {
                      const parts = memberPivot.combos
                        .map(combo => {
                          const [plan, type] = combo.split('||');
                          return { label: `${plan} ${type}`, v: r[`${m}||${combo}`] };
                        })
                        .filter(p => p.v);
                      if (!parts.length) return <span style={{ color: '#d9d9d9' }}>-</span>;
                      // One plan/type is the common case; repeating the figure as
                      // a "total" underneath would just be the same number twice.
                      const total = r[`__m__${m}`];
                      return (
                        <div style={{ lineHeight: 1.3 }}>
                          <div><Text strong><MoneyCell v={parts.length === 1 ? parts[0].v : total} /></Text></div>
                          {parts.map(p => (
                            <div key={p.label} style={{ fontSize: 11, color: '#8c8c8c' }}>
                              {p.label}{parts.length > 1 ? ' ' : ''}
                              {parts.length > 1 && <MoneyCell v={p.v} />}
                            </div>
                          ))}
                        </div>
                      );
                    },
                  })),
                  {
                    title: 'Total', dataIndex: 'Total', key: 'Total',
                    width: 120, align: 'right', fixed: 'right',
                    sorter: (a, b) => a.Total - b.Total,
                    render: v => <Text strong><MoneyCell v={v} /></Text>,
                  },
                ]}
              />
            )
          ) : filterBy === 'summary' ? (
            summaryLoading ? <Spin /> : summary.length === 0 ? (
              <Alert type="info" showIcon message="No invoices uploaded yet." />
            ) : (
              <Table
                rowKey="InvoiceNumber"
                dataSource={summary}
                size="small"
                bordered
                pagination={false}
                scroll={{ x: 'max-content', y: 600 }}
                columns={[
                  { title: 'Date Billed', dataIndex: 'DateBilled', key: 'DateBilled', width: 120,
                    defaultSortOrder: 'descend',
                    sorter: (a, b) => (a.DateBilled || '').localeCompare(b.DateBilled || '') },
                  { title: 'Invoice #', dataIndex: 'InvoiceNumber', key: 'InvoiceNumber', width: 160,
                    sorter: (a, b) => (a.InvoiceNumber || '').localeCompare(b.InvoiceNumber || ''),
                    render: v => v || <Text type="secondary">-</Text> },
                  { title: 'Charge Months', key: 'months', width: 170,
                    render: (_, r) => r.FirstMonth === r.LastMonth
                      ? (r.FirstMonth || <Text type="secondary">-</Text>)
                      : `${r.FirstMonth} - ${r.LastMonth}` },
                  { title: 'Plan', key: 'plan', width: 130,
                    render: (_, r) => (
                      <>
                        {r.BCBSRows > 0 && <Tag color="blue">BCBS {r.BCBSRows}</Tag>}
                        {r.BCNRows  > 0 && <Tag color="purple">BCN {r.BCNRows}</Tag>}
                      </>
                    ) },
                  { title: 'Members', dataIndex: 'Members', key: 'Members', width: 90, align: 'right',
                    sorter: (a, b) => a.Members - b.Members },
                  { title: 'Rows', dataIndex: 'Rows', key: 'Rows', width: 80, align: 'right',
                    sorter: (a, b) => a.Rows - b.Rows },
                  { title: 'Current / Adj', key: 'types', width: 120, align: 'right',
                    render: (_, r) => `${r.CurrentRows} / ${r.AdjustmentRows}` },
                  { title: 'Unattributed', dataIndex: 'Unattributed', key: 'Unattributed', width: 120, align: 'right',
                    sorter: (a, b) => a.Unattributed - b.Unattributed,
                    render: v => v > 0 ? <Tag color="orange">{v}</Tag> : <Text type="secondary">0</Text> },
                  { title: 'Net Amount', dataIndex: 'NetAmount', key: 'NetAmount', width: 130, align: 'right',
                    sorter: (a, b) => (a.NetAmount ?? 0) - (b.NetAmount ?? 0),
                    render: v => <MoneyCell v={v} /> },
                  { title: 'Uploaded', dataIndex: 'UploadedAt', key: 'UploadedAt', width: 160,
                    sorter: (a, b) => (a.UploadedAt || '').localeCompare(b.UploadedAt || ''),
                    render: v => v ? String(v).replace('T', ' ').substring(0, 16) : '-' },
                ]}
              />
            )
          ) : chargesLoading ? <Spin /> : !activeFilter ? (
            <Text type="secondary">
              Select a {filterBy === 'charge_month' ? 'charge month' : 'date billed'} to view charges.
            </Text>
          ) : charges.length === 0 ? (
            <Alert type="info" showIcon message={`No charges stored for ${activeFilter}.`} />
          ) : (
            <Table
              rowKey="ID"
              dataSource={charges}
              size="small"
              pagination={false}
              bordered
              scroll={{ x: 'max-content', y: 600 }}
              columns={[
                { title: 'Member', key: 'member', width: 190, render: (_, r) => memberCell(r) },
                {
                  title: 'QA User',
                  key: 'qaUser',
                  width: 190,
                  render: (_, r) => (
                    <div>
                      {r.QAUserName ? (
                        <span>
                          {r.QAUserName}
                        </span>
                      ) : <Tag color="orange">Unattributed</Tag>}
                      <Button
                        type="link"
                        size="small"
                        icon={<EditOutlined />}
                        style={{ padding: '0 4px' }}
                        onClick={() => openRelink(r)}
                        title="Change who this member is linked to"
                      />
                      <PaycorState status={r.PaycorStatus} termDate={r.PaycorTermDate} />
                    </div>
                  ),
                },
                { title: 'Plan', dataIndex: 'PlanType',   key: 'PlanType',   width: 80,  render: v => <PlanTag v={v} /> },
                { title: 'Type', dataIndex: 'ChargeType', key: 'ChargeType', width: 110, render: v => <TypeTag v={v} /> },
                { title: 'Amount', dataIndex: 'Amount', key: 'Amount', width: 110, align: 'right',
                  render: v => <MoneyCell v={v} /> },
                { title: 'Package', dataIndex: 'BenefitPackage', key: 'BenefitPackage', width: 110,
                  render: v => v || <Text type="secondary">—</Text> },
                { title: 'Coverage', dataIndex: 'RateTierDetail', key: 'RateTierDetail', width: 180,
                  ellipsis: true, render: v => v || <Text type="secondary">—</Text> },
                { title: 'Period', key: 'period', width: 180,
                  render: (_, r) => r.PeriodFrom
                    ? `${String(r.PeriodFrom).substring(0, 10)} → ${r.PeriodThrough ? String(r.PeriodThrough).substring(0, 10) : '?'}`
                    : <Text type="secondary">—</Text> },
                { title: 'Remarks', dataIndex: 'Remarks', key: 'Remarks', width: 90,
                  render: v => v || <Text type="secondary">—</Text> },
                { title: 'Date Billed', dataIndex: 'DateBilled', key: 'DateBilled', width: 110,
                  render: v => v ? String(v).substring(0, 10) : <Text type="secondary">—</Text> },
                { title: 'Invoice #', dataIndex: 'InvoiceNumber', key: 'InvoiceNumber', width: 120,
                  render: v => <Text type="secondary" style={{ fontSize: 11, fontFamily: 'monospace' }}>{v || '—'}</Text> },
              ]}
            />
          )}
        </>
      )}

      <Modal maskClosable={false}
        title="Change member link"
        open={!!relinkRow}
        onCancel={() => setRelinkRow(null)}
        confirmLoading={relinkSaving}
        onOk={() => submitRelink(relinkUserId)}
        okText="Save"
        okButtonProps={{ disabled: !relinkUserId }}
      >
        {relinkRow && (
          <>
            <Descriptions size="small" column={1} style={{ marginBottom: 12 }}>
              <Descriptions.Item label="Member">
                {relinkRow.LastName}, {relinkRow.FirstName}
              </Descriptions.Item>
              <Descriptions.Item label="Currently linked to">
                {relinkRow.QAUserName || <Text type="secondary">Nobody</Text>}
              </Descriptions.Item>
            </Descriptions>
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message="This covers every charge for this member, past months included."
            />
            <Select
              showSearch
              allowClear
              optionFilterProp="label"
              style={{ width: '100%' }}
              placeholder="Select the correct QA user"
              options={qaUserOptions}
              value={relinkUserId}
              onChange={setRelinkUserId}
              loading={qaUsersLoading}
            />
            {relinkRow.QAUserName && (
              <Popconfirm
                title="Clear this link?"
                description="The member's charges go back to Unattributed."
                onConfirm={() => submitRelink(null)}
                okText="Clear"
              >
                <Button danger type="link" style={{ paddingLeft: 0, marginTop: 8 }}>
                  Clear link instead
                </Button>
              </Popconfirm>
            )}
          </>
        )}
      </Modal>
    </div>
  );
}
